import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomToastService } from 'src/app/core/services/custom-toast.service';
import { MasterService } from 'src/app/core/services/master.service';
import { ErrorHandler } from 'src/app/core/utils/error-handler.util';
import {
  CreateUserPasscodeModel,
  Permission,
  Status,
  UserPasscodeResponse,
  UserPermissions,
} from '../../models/shared.models';
import { SharedService } from '../../services/shared.service';
import { UsersManagementService } from 'src/app/features/company-portal/modules/user-management/services/user-management.service';
import { UserPermissionsModel } from 'src/app/features/company-portal/modules/user-management/models/user-management.models';

@Component({
  selector: 'app-user-passcode-settings',
  templateUrl: './user-passcode-settings.component.html',
  styleUrls: ['./user-passcode-settings.component.css'],
  standalone: false,
})
export class UserPasscodeSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('closePasscodeSheetBtn') closePasscodeSheetBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('passcodeSearchInput') passcodeSearchInput?: ElementRef<HTMLInputElement>;

  selectedStatusFilter: 'active' | 'inactive' | 'all' = 'active';
  selectedPasscodeId = 0;
  isEditMode = false;
  isSaving = false;
  formSubmitted = false;
  isStatusSaving = false;
  searchText = '';
  isSearchReadonly = true;

  showFormPasscode = false;
  visiblePasscodes: Record<number, boolean> = {};
  filteredPermissionIds: number[] = [];

  passcodeRows: UserPasscodeResponse[] = [];
  private readonly searchClearDelays = [0, 250, 800];
  private pageShowHandler = () => this.resetSearchInput();

  /** When navigating from the users table, these are set from query params */
  targetUserId = 0;
  targetUserName = '';
  isManagingOtherUser = false;

  passcodeForm: CreateUserPasscodeModel = {
    companyId: 0,
    userId: 0,
    name: '',
    passcode: '',
    expiresAt: null,
    permissionIds: [],
  };

  private readonly requiredPermissionNames = new Set([
    'PointOfSale:ItemOverride',
    'PointOfSale:ApplyDiscount',
    'PointOfSale:VoidTransaction',
    'PointOfSale:ProcessReturn',
    'PointOfSale:OverrideThresholdAmount',
    'PointOfSale:ViewTaxableAmount',
  ]);

  constructor(
    private sharedService: SharedService,
    private masterService: MasterService,
    private route: ActivatedRoute,
    private router: Router,
    private toast: CustomToastService,
    private usersManagementService: UsersManagementService,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const queryUserId = Number(params.get('userId') || 0);
    const loggedInUserId = this.getCurrentUser().userid;

    if (queryUserId > 0 && queryUserId !== loggedInUserId) {
      this.targetUserId = queryUserId;
      this.targetUserName = params.get('userName') || '';
      this.isManagingOtherUser = true;
    } else {
      this.targetUserId = loggedInUserId;
      this.isManagingOtherUser = false;
    }

    this.initializeUserContext();
    this.loadRequiredPermissionIds();
    this.loadUserPasscodes();
  }

  ngAfterViewInit(): void {
    this.scheduleSearchInputReset();
    window.addEventListener('pageshow', this.pageShowHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('pageshow', this.pageShowHandler);
  }

  enableSearchInput(): void {
    if (!this.isSearchReadonly) {
      return;
    }

    this.isSearchReadonly = false;
    this.resetSearchInput();
  }

  goBack(): void {
    this.router.navigate(['/company-portal/user-management/users']);
  }

  get filteredPasscodeRows(): UserPasscodeResponse[] {
    const normalizedSearch = this.searchText.trim().toLowerCase();

    return this.passcodeRows.filter((row) => {
      const matchesStatus =
        this.selectedStatusFilter === 'all' ||
        (this.selectedStatusFilter === 'active' && row.isActive) ||
        (this.selectedStatusFilter === 'inactive' && !row.isActive);

      const matchesSearch =
        !normalizedSearch || row.name.toLowerCase().includes(normalizedSearch);

      return matchesStatus && matchesSearch;
    });
  }

  setStatusFilter(filter: 'active' | 'inactive' | 'all'): void {
    this.selectedStatusFilter = filter;
  }

  openCreatePasscodeSheet(): void {
    this.isEditMode = false;
    this.selectedPasscodeId = 0;
    this.formSubmitted = false;
    this.showFormPasscode = false;
    this.passcodeForm = {
      companyId: this.getCurrentUser().companyid,
      userId: this.targetUserId,
      name: '',
      passcode: '',
      expiresAt: null,
      permissionIds: [],
    };
  }

  openEditPasscodeSheet(row: UserPasscodeResponse): void {
    this.isEditMode = true;
    this.selectedPasscodeId = row.id;
    this.formSubmitted = false;
    this.showFormPasscode = false;
    this.passcodeForm.companyId = this.getCurrentUser().companyid;
    this.passcodeForm.userId = this.targetUserId;
    this.passcodeForm.name = row.name;
    this.passcodeForm.passcode = row.passcode;
    this.passcodeForm.expiresAt = this.toDateInputValue(row.expiresAt);
  }

  saveOrUpdatePasscode(): void {
    this.formSubmitted = true;

    if (!this.passcodeForm.name.trim()) {
      return;
    }

    const passcodeVal = this.passcodeForm.passcode.trim();
    if (!passcodeVal || !/^[0-9]{4,6}$/.test(passcodeVal)) {
      return;
    }

    if (!this.passcodeForm.expiresAt) {
      return;
    }

    const payload: CreateUserPasscodeModel = {
      ...this.passcodeForm,
      name: this.passcodeForm.name.trim(),
      passcode: this.passcodeForm.passcode.trim(),
      permissionIds: [...this.filteredPermissionIds],
      expiresAt: this.toUtcIsoString(this.passcodeForm.expiresAt),
    };

    this.isSaving = true;

    if (this.isEditMode && this.selectedPasscodeId > 0) {
      this.sharedService.updateUserPasscode(this.selectedPasscodeId, payload).subscribe({
        next: () => {
          this.isSaving = false;
          this.toast.success('Passcode updated successfully.');
          this.closePasscodeSideSheet();
          this.loadUserPasscodes();
        },
        error: (error) => {
          this.isSaving = false;
          this.handlePasscodeSaveError(error, 'Failed to update passcode. Please try again.');
        },
      });
      return;
    }

    this.sharedService.createUserPasscode(payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.toast.success('Passcode created successfully.');
        this.closePasscodeSideSheet();
        this.loadUserPasscodes();
      },
      error: (error) => {
        this.isSaving = false;
        this.handlePasscodeSaveError(error, 'Failed to create passcode. Please try again.');
      },
    });
  }

  onChangePasscodeStatus(row: UserPasscodeResponse, event: Event): void {
    if (this.isStatusSaving) {
      return;
    }

    const target = event.target as HTMLInputElement;
    const previousState = row.isActive ?? false;
    const nextState = target.checked;

    row.isActive = nextState;
    this.isStatusSaving = true;

    this.sharedService.changeUserPasscodeStatus(row.id, nextState).subscribe({
      next: () => {
        this.isStatusSaving = false;
      },
      error: () => {
        row.isActive = previousState;
        target.checked = previousState;
        this.isStatusSaving = false;
      },
    });
  }

  togglePasscodeVisibility(rowId: number): void {
    this.visiblePasscodes[rowId] = !this.visiblePasscodes[rowId];
  }

  isPasscodeVisible(rowId: number): boolean {
    return !!this.visiblePasscodes[rowId];
  }

  getDisplayedPasscode(row: UserPasscodeResponse): string {
    if (this.isPasscodeVisible(row.id)) {
      return row.passcode || '';
    }

    return '*'.repeat(Math.max((row.passcode || '').length, 6));
  }

  private initializeUserContext(): void {
    this.passcodeForm.companyId = this.getCurrentUser().companyid;
    this.passcodeForm.userId = this.targetUserId;
  }

  private scheduleSearchInputReset(): void {
    this.searchClearDelays.forEach((delay) => {
      setTimeout(() => this.resetSearchInput(), delay);
    });
  }

  private resetSearchInput(): void {
    const input = this.passcodeSearchInput?.nativeElement;

    if (!input) {
      return;
    }

    input.value = '';
    this.searchText = '';
  }

  private handlePasscodeSaveError(error: any, fallbackMessage: string): void {
    const errorMessage = ErrorHandler.extractErrorMessage(error) || fallbackMessage;
    const statusCode = ErrorHandler.getStatusCode(error);

    if (ErrorHandler.isServerError(error)) {
      this.toast.serverError(errorMessage, statusCode);
      return;
    }

    this.toast.clientError(errorMessage, statusCode);
  }

  private loadRequiredPermissionIds(): void {
    if (this.isManagingOtherUser) {
      // For other users, call /Users/{id}/Permissions which returns grouped permissions
      this.usersManagementService.getUserPermissionsByUserId(this.targetUserId).subscribe((res: any) => {
        const typed = res as UserPermissionsModel[];
        const allPermissions = (typed || []).flatMap((app) =>
          app.permissionGroups.flatMap((group) => group.permissions),
        );

        const seen = new Set<number>();
        this.filteredPermissionIds = allPermissions
          .filter((p) => {
            if (this.requiredPermissionNames.has(p.name) && !seen.has(p.id)) {
              seen.add(p.id);
              return true;
            }
            return false;
          })
          .map((p) => p.id);
      });
    } else {
      // For the logged-in user, use the existing Roles endpoint
      this.sharedService.getPermissionsByUserId(this.targetUserId).subscribe((res: UserPermissions[]) => {
        const uniquePermissions = (res || []).reduce((acc, role) => {
          role.permissions?.forEach((permission: Permission) => {
            if (!acc.some((p) => p.id === permission.id)) {
              acc.push(permission);
            }
          });
          return acc;
        }, [] as Permission[]);

        this.filteredPermissionIds = uniquePermissions
          .filter((permission) => this.requiredPermissionNames.has(permission.name))
          .map((permission) => permission.id);
      });
    }
  }

  private loadUserPasscodes(): void {
    const { companyid } = this.getCurrentUser();

    this.sharedService.getUserPasscodes(companyid, this.targetUserId).subscribe((res) => {
      const rows = Array.isArray(res) ? res : res?.items || [];
      this.passcodeRows = rows.map((item: UserPasscodeResponse) => ({
        ...item,
        isActive:
          typeof item.isActive === 'boolean'
            ? item.isActive
            : item.status === Status.Active,
      }));
    });
  }

  private closePasscodeSideSheet(): void {
    this.closePasscodeSheetBtn?.nativeElement.click();
  }

  private getCurrentUser(): { userid: number; companyid: number } {
    const user = this.masterService.getUserDetailFromStorage();
    return {
      userid: Number(user?.userid || 0),
      companyid: Number(user?.companyid || 0),
    };
  }

  private toDateInputValue(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    const y = parsed.getUTCFullYear();
    const m = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const d = String(parsed.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private toUtcIsoString(value: string | null): string | null {
    if (!value) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day, 0, 0, 0)).toISOString();
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
}
